import { Injectable, OnDestroy } from '@angular/core';
import { environment } from 'src/environments/environment.development';
import { BaseService } from './base.service';
import {
  iNotificationDto,
  iNotificationRequest,
  iNotificationResponse,
} from './Interfaces/notifications';
import * as signalR from '@microsoft/signalr';
import {
  BehaviorSubject,
  finalize,
  map,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService extends BaseService implements OnDestroy {
  private readonly _getAll = `${environment.base_url}/api/NotificationsToSend/GetAll`;
  private readonly _getById = `${environment.base_url}/api/NotificationsToSend/GetById`;
  private readonly _readNotification = `${environment.base_url}/api/NotificationsToSend/ReadNotification`;
  private readonly _add = `${environment.base_url}/api/NotificationsToSend/Add`;
  private readonly _addRange = `${environment.base_url}/api/NotificationsToSend/AddRange`;
  private readonly _getForEmployee = `${environment.base_url}/api/NotificationsToSend/GetForEmployee`;
  private readonly _remove = `${environment.base_url}/api/NotificationsToSend/Remove`;
  private readonly _hubUrl = `${environment.base_url}/newNotificationHub`;

  private _connection = new signalR.HubConnectionBuilder()
    .withUrl(this._hubUrl)
    .configureLogging(signalR.LogLevel.None)
    .build();

  private audio: HTMLAudioElement;
  private isConnectionStarting = false;
  private _notificationsSource: BehaviorSubject<iNotificationDto[]>;
  private _notificationsLoadingSource: BehaviorSubject<boolean>;
  private _unsubscribeAll = new Subject();

  public notifications$: Observable<iNotificationDto[]>;
  public notificationsLoading$: Observable<boolean>;

  get notificationsValue(): iNotificationDto[] {
    return this._notificationsSource.value;
  }

  constructor(private _toastrServ: ToastrService) {
    super();

    this.audio = new Audio('../../../../../assets/audio/notification.mp3');
    this.audio.load();

    this._notificationsSource = new BehaviorSubject<iNotificationDto[]>([]);
    this.notifications$ = this._notificationsSource.asObservable();
    this._notificationsLoadingSource = new BehaviorSubject(false);
    this.notificationsLoading$ =
      this._notificationsLoadingSource.asObservable();

    this._connection.onclose(async () => {
      await this._start();
    });
    this._setupNotification();
  }
  ngOnDestroy(): void {
    if (this._connection.state === 'Connected') {
      this._connection.stop().then(() => {
        console.warn('SignalR _connection stopped');
      });
    }
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  getAll() {
    return this._http.get<iNotificationResponse[]>(`${this._getAll}`, {
      responseType: 'json',
    });
  }
  getById(id: string) {
    return this._http.get<iNotificationResponse>(`${this._getById}`, {
      responseType: 'json',
      params: { id },
    });
  }
  add(payload: iNotificationRequest) {
    return this._http.post<iNotificationResponse>(`${this._add}`, payload, {
      responseType: 'json',
    });
  }
  addRange(payload: iNotificationRequest[]) {
    return this._http.post<iNotificationResponse[]>(
      `${this._addRange}`,
      payload,
      {
        responseType: 'json',
      }
    );
  }
  getForEmployee() {
    const EmpId = this._dataServ.employeeValue.id;
    return this._http.get<iNotificationDto[]>(`${this._getForEmployee}`, {
      responseType: 'json',
      params: { EmpId },
    });
  }
  remove(id: string) {
    return this._http.delete(`${this._remove}`, {
      responseType: 'json',
      params: { id },
    });
  }
  readNotification(notificationIds: string[]) {
    return this._http
      .post(
        `${this._readNotification}`,
        { notificationIds, employeeId: this._dataServ.employeeValue.id },
        {
          responseType: 'json',
        }
      )
      .pipe((res) => {
        const notifications = [...this.notificationsValue];
        notifications.forEach((x) => {
          if (notificationIds.includes(x.id)) {
            x.isReaded = true;
          }
        });
        this._notificationsSource.next(notifications);
        return res;
      });
  }

  private _fetchNotifications() {
    this._notificationsLoadingSource.next(true);
    this.getForEmployee()
      .pipe(
        map((res) => res.map(this._processNotification.bind(this))),
        takeUntil(this._unsubscribeAll),
        finalize(() => {
          this._notificationsLoadingSource.next(false);
        })
      )
      .subscribe({
        next: (res) => {
          this._notificationsSource.next(res.reverse());
        },
        error: (er) => {
          console.warn(er);
          this._toastrServ.error(
            'Something went wrong while fetching notifications.'
          );
        },
      });
  }
  private async _start() {
    if (
      this._dataServ.tokenValue &&
      this._connection.state === 'Disconnected' &&
      !this.isConnectionStarting
    ) {
      try {
        this.isConnectionStarting = true;
        await this._connection.start();
        // console.log('SignalR _connection started');
      } catch (err) {
        console.error('SignalR _connection error:', err);
        setTimeout(() => this._start(), 5000);
      } finally {
        this.isConnectionStarting = false;
      }
    }
  }
  private _setupNotification() {
    this._connection.on('ReceiveNewNotification', (res: iNotificationDto) => {
      const show = res.notificationEmployees.some(
        (x) => x.employeeId == this._dataServ.employeeValue.id
      );
      if (show) {
        this.playNotificationSound();
        this._showNotification(res);
        this._notificationsSource.next([
          this._processNotification(res),
          ...this.notificationsValue,
        ]);
      }
    });
    this._dataServ.employee$.subscribe((employee) => {
      if (employee) {
        this._fetchNotifications();
        this._start();
      }
    });
  }

  private playNotificationSound() {
    this.audio.play().catch((err) => {
      console.error(
        'Audio playback failed:',
        err,
        'Tab focused:',
        document.visibilityState === 'visible'
      );
    });
  }
  private async _showNotification(data: iNotificationDto) {
    if (!window.Notification) {
      console.warn('Browser does not support notifications.');
      this._toastrServ.warning('Browser does not support notifications.');
      return;
    }

    const createNotification = () => {
      const textOnly = data.title.replace(/<[^>]*>/g, '');
      const notification = new Notification(textOnly, {
        body: data.content,
        icon: '/assets/images/logo/osquare-pixel.png',
        silent: true,
      });

      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
      };
    };

    try {
      if (Notification.permission === 'granted') {
        createNotification();
      } else {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          createNotification();
        } else {
          this._toastrServ.warning(
            'Notification permission denied. Please enable notifications in your browser.'
          );
        }
      }
    } catch (err) {
      console.error('Notification error:', err);
      this._toastrServ.error('Failed to show notification.');
    }
  }
  private _processNotification(notification: iNotificationDto) {
    const ne = notification.notificationEmployees.find(
      (em) => em.employeeId == this._dataServ.employeeValue.id
    );
    notification.isReaded = ne ? ne.isReaded : true;
    return notification;
  }
}
